/**
 * Shared Commercial PA domain types — single source of truth for
 * @cortex/engine (server-side rating) and @cortex/web (UI).
 *
 * Consolidation of the duplicated types that previously lived in
 * packages/engine/src/types.ts AND apps/web/types.ts.
 *
 * PricingSummary is intentionally kept in quote.ts (it is part of the
 * quote wire contract); import it from @cortex/domain directly.
 */

import { z } from 'zod';

// ─── Core enums ───────────────────────────────────────────────────────────────

export enum RiskClass {
  Risk1 = 'Risk 1 (Low)',
  Risk2 = 'Risk 2 (Medium)',
  Risk3 = 'Risk 3 (High)',
  Risk4 = 'Risk 4 (Extreme)',
}

export enum BenefitType {
  Death = 'Death',
  PTD = 'PTD',
  TTD = 'TTD',
  Medical = 'Medical',
}

export enum CalculationMethod {
  Stated = 'Stated Benefits (Wageroll)',
  Fixed = 'Fixed GPA (Headcount)',
}

export enum BasisOfCover {
  TwentyFourHour = '24 Hours',
  WorkingHours = 'Working Hours Only',
  WorkingCommuting = 'Working Hours + Commuting',
  Other = 'Other',
}

export enum Currency {
  ZAR = 'ZAR',
  USD = 'USD',
  NAD = 'NAD',
  MUR = 'MUR',
}

/**
 * Legacy display-name enum mapping real CORTEX role slugs to underwriter
 * tiers. Used by the sidebar and workbench for permission gating until
 * the React tree is migrated to `AuthPrincipal` directly (planned post-v1).
 *
 * For the runtime role-assignment record shape see `UserRoleAssignment`.
 */
export enum UserRole {
  Superuser = 'System Administrator',
  Senior = 'Senior Underwriter',
  Junior = 'Junior Underwriter',
  Broker = 'Broker / Intermediary',
}

/** All CORTEX product lines. CommercialPA is the sole active v1 line. */
export enum ProductType {
  CommercialPA = 'Commercial PA',
  CorporatePA = 'Corporate PA',
  FilmMediaPA = 'Film & Media PA',
  AviationPA = 'Aviation PA',
  ShortPeriodPA = 'Short Period PA',
  BusinessTravel = 'Business Travel',
  OnceOffTravel = 'Once-Off Travel',
  InboundTravel = 'Inbound Travel',
  LocalTravel = 'Local Travel',
  MedicalEvacuation = 'Medical Evacuation',
  KidnapRansom = 'Kidnap & Ransom',
}

export const RiskClassSchema = z.nativeEnum(RiskClass);
export const ProductTypeSchema = z.nativeEnum(ProductType);
export const BenefitTypeSchema = z.nativeEnum(BenefitType);
export const CalculationMethodSchema = z.nativeEnum(CalculationMethod);
export const BasisOfCoverSchema = z.nativeEnum(BasisOfCover);
export const CurrencySchema = z.nativeEnum(Currency);
export const UserRoleSchema = z.nativeEnum(UserRole);

// ─── Engine configuration types ───────────────────────────────────────────────

/**
 * Engine / UI view of a sector — the display and risk defaults used by
 * the workbench. Distinct from `SectorDto` (the admin-editable DB record).
 */
export const SectorConfigSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  defaultRisk: RiskClassSchema,
  /** Short, dropdown-friendly summary shown next to the sector picker. */
  description: z.string(),
  /** Full narrative shown in the "more info" disclosure. */
  longDescription: z.string(),
  drivingRisk: z.boolean(),
});

export const RateTableSchema = z.object({
  baseRates: z.record(z.string(), z.number()),
});

export const MedicalMatrixEntrySchema = z.object({
  limit: z.number(),
  rates: z.record(z.string(), z.number()),
});

export const SanityBandSchema = z.object({
  min: z.number(),
  max: z.number(),
  threshold: z.number(),
});

export const RolePermissionDefaultsSchema = z.object({
  authorityLimit: z.number(),
  canBindQuote: z.boolean(),
  canAuthorizeReferral: z.boolean(),
  canViewAllQuotes: z.boolean(),
  canEditRates: z.boolean(),
});

export const LombardEngineConfigSchema = z.object({
  version: z.string().min(1),
  lastUpdated: z.string(),
  roleDefaults: z.record(z.string(), RolePermissionDefaultsSchema),
  sectors: z.array(SectorConfigSchema),
  rates: z.object({
    Death: RateTableSchema,
    PTD: RateTableSchema,
    TTD_Stated: RateTableSchema,
    TTD_Fixed: RateTableSchema,
    MedicalGPA: z.array(MedicalMatrixEntrySchema),
    MedicalSB: z.array(MedicalMatrixEntrySchema),
  }),
  loads: z.object({
    extremeRiskLoad: z.record(z.string(), z.number()),
  }),
  factors: z.object({
    ttdPeriod: z.record(z.string(), z.number()),
    ttdExcess: z.record(z.string(), z.number()),
    medicalExcess: z.record(z.string(), z.number()),
    basisOfCover: z.record(z.string(), z.number()),
  }),
  volumeDiscounts: z.array(
    z.object({ minLives: z.number(), maxLives: z.number(), discount: z.number() }),
  ),
  volumeDiscountsWageroll: z.array(
    z.object({ minWageroll: z.number(), maxWageroll: z.number(), discount: z.number() }),
  ),
  sanityThresholds: z.record(z.string(), SanityBandSchema),
  governance: z.object({
    maxAccumulation: z.number(),
    minPremium: z.number(),
  }),
});

// ─── Quote building blocks ─────────────────────────────────────────────────────

export const BenefitConfigSchema = z.object({
  id: z.string().uuid(),
  type: BenefitTypeSchema,
  isEnabled: z.boolean(),
  multiple: z.number().optional(),
  salaryPct: z.number().optional(),
  sumInsured: z.number().optional(),
  weeklyBenefit: z.number().optional(),
  benefitPeriod: z.union([z.literal(52), z.literal(104), z.literal(156)]).optional(),
  excessDays: z.union([z.literal(7), z.literal(14), z.literal(28), z.literal(60)]).optional(),
  medicalLimit: z.number().optional(),
  medicalExcess: z
    .union([
      z.literal(0),
      z.literal(250),
      z.literal(500),
      z.literal(1000),
      z.literal(2500),
      z.literal(5000),
    ])
    .optional(),
  medicalBasis: z.enum(['Fixed', 'Stated']).optional(),
  discretionaryAdjustment: z.number().optional(),
  grossRate: z.number().optional(),
  netPremium: z.number(),
  systemFactors: z.array(z.object({ label: z.string(), val: z.number() })).optional(),
});

export const CategorySchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  method: CalculationMethodSchema,
  riskClass: RiskClassSchema,
  basis: BasisOfCoverSchema,
  customBasisDescription: z.string().optional(),
  customBasisAdjustment: z.number().optional(),
  discretionaryAdjustment: z.number().optional(),
  headcount: z.number().int().nonnegative(),
  wageroll: z.number().nonnegative(),
  benefits: z.array(BenefitConfigSchema),
  systemBasisFactor: z.number().optional(),
});

// ─── Exported TypeScript types ─────────────────────────────────────────────────

export type SectorConfig = z.infer<typeof SectorConfigSchema>;
export type RateTable = z.infer<typeof RateTableSchema>;
export type MedicalMatrixEntry = z.infer<typeof MedicalMatrixEntrySchema>;
export type SanityBand = z.infer<typeof SanityBandSchema>;
export type RolePermissionDefaults = z.infer<typeof RolePermissionDefaultsSchema>;
export type LombardEngineConfig = z.infer<typeof LombardEngineConfigSchema>;
export type BenefitConfig = z.infer<typeof BenefitConfigSchema>;
export type Category = z.infer<typeof CategorySchema>;
